import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  User, MessageSquare, Calendar, LogOut, Sparkles,
  Briefcase, Headphones, PenTool, CalendarDays, TrendingUp, Shield, Activity, Building2, BadgePercent, Upload, FileText, Wallet, Newspaper, BellRing, Mail, Cable
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/SEO';
import WorkspaceHub from '../../components/workspace/WorkspaceHub';
import { TerritoryExecutiveMap } from '../../components/admin/TerritoryExecutiveMap';
import PartnerGrowthOffice from '../../components/partner/PartnerGrowthOffice';
import { PasswordInput } from '../../components/ui/password-input';
import Neuralia369TesixHub from '../../components/admin/Neuralia369TesixHub';
import EmbeddedConnectionsHub from '../../components/connections/EmbeddedConnectionsHub';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_ICONS = {
  COMERCIAL: Briefcase, SOPORTE: Headphones, CONTENIDO: PenTool,
  CITAS: CalendarDays, VENTAS: TrendingUp, ADMIN: Shield, GENERAL: Sparkles
};
const CHART_COLORS = ['#00E5FF', '#4361EE', '#F72585', '#22C55E', '#EAB308', '#A855F7'];

const STATUS_STYLES = {
  active: 'bg-green-500/15 text-green-300 border-green-500/20',
  activo: 'bg-green-500/15 text-green-300 border-green-500/20',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/20',
  pendiente: 'bg-amber-500/15 text-amber-300 border-amber-500/20',
  draft: 'bg-slate-500/15 text-slate-300 border-slate-500/20',
  sent: 'bg-blue-500/15 text-blue-300 border-blue-500/20',
  viewed: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/20',
  signed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
  declined: 'bg-red-500/15 text-red-300 border-red-500/20',
  failed: 'bg-red-500/15 text-red-300 border-red-500/20',
};

const getStatusClass = (status) => STATUS_STYLES[(status || '').toLowerCase()] || 'bg-card text-text-muted border-border-subtle';

const PATIENT_PORTAL_VIEWS = [
  { id: 'resumen', label: 'Estado actual', helper: 'Qué está pasando ahora y cuál es tu siguiente paso.' },
  { id: 'casos', label: 'Mis casos', helper: 'Estado clínico, etapas y simulaciones por caso.' },
  { id: 'novedades', label: 'Agenda y novedades', helper: 'Citas, recomendaciones, documentos y recordatorios.' },
  { id: 'simulacion', label: 'Simulación IA', helper: 'Sube una foto y genera una visualización orientativa.' },
];

const getPatientCaseLabel = (lead) => `${lead.treatment_interest || 'Caso clínico'} · ${lead.stage || 'Pendiente'}`;

const buildPartnerTerritoryMapItems = (partnerExclusivity = {}) => {
  const rollupMap = new Map((partnerExclusivity.country_rollup || []).map((item) => [item.country, item.count]));
  return (partnerExclusivity.countries || []).map((country) => ({
    country,
    leadCount: rollupMap.get(country) || 0,
    routedCount: rollupMap.get(country) || 0,
    exclusive: true,
    ownerLabel: 'Tu exclusividad activa',
  }));
};

const TABS = [
  { id: 'workspace', label: 'Workspace', icon: Sparkles },
  { id: 'connections', label: 'Conexiones', icon: Cable },
  { id: 'overview', label: 'Mi Resumen', icon: Activity },
  { id: 'profile', label: 'Mi Perfil', icon: User },
  { id: 'bookings', label: 'Mis Citas', icon: Calendar },
];

const PARTNER_TABS = [
  { id: 'office', label: 'Virtual Office', icon: Sparkles },
  { id: 'connections', label: 'Conexiones', icon: Cable },
  { id: 'overview', label: 'Mi Resumen', icon: Activity },
  { id: 'workspace', label: 'Workspace', icon: Briefcase },
  { id: 'profile', label: 'Mi Perfil', icon: User },
  { id: 'bookings', label: 'Mis Citas', icon: Calendar },
];

const PATIENT_TABS = [
  { id: 'overview', label: 'Portal Clínico', icon: Sparkles },
  { id: 'profile', label: 'Mi Perfil', icon: User },
  { id: 'bookings', label: 'Mis Citas', icon: Calendar },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-[#0A1A3F] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 shadow-lg">
        <p className="text-gray-400 text-xs">{label}</p>
        <p className="text-[#00E5FF] font-bold text-sm">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

export default function UserDashboard() {
  const { user, authFetch, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [topUpLoading, setTopUpLoading] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [financeForm, setFinanceForm] = useState({ title: '', document_type: 'invoice', amount: '', currency: 'EUR', issue_date: '', due_date: '', counterparty: '', notes: '', document_file: null });
  const [financeNote, setFinanceNote] = useState({ title: '', content: '', category: 'cash_flow' });
  const [financeLoading, setFinanceLoading] = useState(false);
  const [stockForm, setStockForm] = useState({ name: '', sku: '', current_units: 0, minimum_units: 0, reorder_units: 0, notes: '' });
  const [purchaseForm, setPurchaseForm] = useState({ title: '', supplier: '', estimated_amount: '', currency: 'EUR', reason: '', category: 'general', approval_mode: 'manual' });
  const [clinicSimulationForm, setClinicSimulationForm] = useState({ lead_id: '', simulation_goal: '', simulation_notes: '', before_image: null });
  const [patientClinicView, setPatientClinicView] = useState('resumen');
  const isPartnerOrAsociado = user?.role === 'partner' || user?.role === 'asociado' || user?.role === 'agencia';
  const isClinicalPatient = user?.role === 'user';
  const activePatientPortalView = PATIENT_PORTAL_VIEWS.find((item) => item.id === patientClinicView) || PATIENT_PORTAL_VIEWS[0];
  const primaryClinicalCase = dashboard?.client_portal?.clinic_case_cards?.[0] || dashboard?.client_portal?.clinic_cases?.[0] || null;
  const primaryUpcomingEvent = dashboard?.client_portal?.clinic_upcoming_events?.[0] || null;
  const primaryClinicalNews = dashboard?.client_portal?.clinic_news?.[0] || null;

  const loadDashboard = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/partner/dashboard`);
      if (r.ok) { setDashboard(await r.json()); }
    } catch (error) { console.error('Error loading partner dashboard', error); }
  }, [authFetch]);

  const loadBookings = useCallback(async () => {
    try { const r = await authFetch(`${API}/user/bookings`); setBookings((await r.json()).bookings); } catch (error) { console.error('Error loading bookings', error); }
  }, [authFetch]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role === 'admin') { navigate('/admin'); return; }
    if (['doctora', 'enfermera', 'administradora', 'community_manager'].includes(user.role)) { navigate('/clinica/panel'); return; }
    loadDashboard();
  }, [user, loading, navigate, loadDashboard]);

  useEffect(() => {
    if (isPartnerOrAsociado) {
      setTab((current) => (current === 'overview' ? 'office' : current));
    }
  }, [isPartnerOrAsociado]);

  useEffect(() => {
    if (tab === 'bookings') loadBookings();
  }, [tab, loadBookings]);

  useEffect(() => {
    const syncTopUpStatus = async () => {
      if (typeof window === 'undefined' || !user) return;
      const params = new URLSearchParams(window.location.search);
      const topupState = params.get('credits_topup');
      const sessionId = params.get('session_id');
      if (topupState === 'cancelled') {
        alert('La recarga se canceló antes del pago.');
        window.history.replaceState({}, '', '/panel');
        return;
      }
      if (topupState !== 'success' || !sessionId) return;
      try {
        const res = await authFetch(`${API}/checkout/status/${sessionId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'No se pudo validar la recarga');
        await loadDashboard();
        alert(data.payment_status === 'paid' ? 'Recarga confirmada correctamente.' : 'Tu recarga se está validando. Verás el saldo actualizado en breve.');
      } catch (error) {
        alert(error.message || 'No se pudo validar la recarga');
      } finally {
        window.history.replaceState({}, '', '/panel');
      }
    };
    syncTopUpStatus();
  }, [user, authFetch, loadDashboard]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      setPasswordLoading(true);
      const res = await authFetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar la contraseña');
      setPasswordForm({ current_password: '', new_password: '' });
      alert('Contraseña actualizada correctamente');
    } catch (error) {
      alert(error.message || 'No se pudo actualizar la contraseña');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const launchTopUpCheckout = async (packId) => {
    try {
      setTopUpLoading(packId);
      const res = await authFetch(`${API}/credits/top-up-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack_id: packId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo iniciar la recarga');
      window.location.href = data.url;
    } catch (error) {
      alert(error.message || 'No se pudo iniciar la recarga');
    } finally {
      setTopUpLoading(null);
    }
  };

  const uploadFinancialDocument = async (e) => {
    e.preventDefault();
    try {
      setFinanceLoading(true);
      const body = new FormData();
      Object.entries(financeForm).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') body.append(key, value);
      });
      const res = await authFetch(`${API}/user/financial-documents`, { method: 'POST', body });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo subir el documento');
      setFinanceForm({ title: '', document_type: 'invoice', amount: '', currency: 'EUR', issue_date: '', due_date: '', counterparty: '', notes: '', document_file: null });
      await loadDashboard();
      alert('Documento subido correctamente');
    } catch (error) {
      alert(error.message || 'No se pudo subir el documento');
    } finally {
      setFinanceLoading(false);
    }
  };

  const createFinanceNote = async (e) => {
    e.preventDefault();
    try {
      setFinanceLoading(true);
      const res = await authFetch(`${API}/user/financial-plan-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(financeNote)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo guardar la nota');
      setFinanceNote({ title: '', content: '', category: 'cash_flow' });
      await loadDashboard();
      alert('Nota financiera guardada');
    } catch (error) {
      alert(error.message || 'No se pudo guardar la nota');
    } finally {
      setFinanceLoading(false);
    }
  };

  const downloadFinancialDocument = async (documentId, filename) => {
    try {
      const res = await authFetch(`${API}/user/financial-documents/${documentId}/download`);
      if (!res.ok) throw new Error('No se pudo descargar el documento');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'documento';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.message || 'No se pudo descargar el documento');
    }
  };

  const createStockItem = async (e) => {
    e.preventDefault();
    try {
      setFinanceLoading(true);
      const res = await authFetch(`${API}/user/stock-items`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...stockForm, current_units: Number(stockForm.current_units), minimum_units: Number(stockForm.minimum_units), reorder_units: Number(stockForm.reorder_units) }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear el item');
      setStockForm({ name: '', sku: '', current_units: 0, minimum_units: 0, reorder_units: 0, notes: '' });
      await loadDashboard();
      alert('Item de stock guardado');
    } catch (error) {
      alert(error.message || 'No se pudo crear el item');
    } finally {
      setFinanceLoading(false);
    }
  };

  const createPurchaseRequest = async (e) => {
    e.preventDefault();
    try {
      setFinanceLoading(true);
      const res = await authFetch(`${API}/user/purchase-requests`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...purchaseForm, estimated_amount: Number(purchaseForm.estimated_amount) }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear la compra');
      setPurchaseForm({ title: '', supplier: '', estimated_amount: '', currency: 'EUR', reason: '', category: 'general', approval_mode: 'manual' });
      await loadDashboard();
      alert('Solicitud de compra guardada');
    } catch (error) {
      alert(error.message || 'No se pudo crear la compra');
    } finally {
      setFinanceLoading(false);
    }
  };

  const updatePurchaseStatus = async (purchaseId, status) => {
    try {
      const res = await authFetch(`${API}/user/purchase-requests/${purchaseId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar');
      await loadDashboard();
      alert('Autorización actualizada');
    } catch (error) {
      alert(error.message || 'No se pudo actualizar');
    }
  };

  const createClinicSimulation = async (e) => {
    e.preventDefault();
    try {
      setFinanceLoading(true);
      if (!clinicSimulationForm.lead_id || !clinicSimulationForm.simulation_goal || !clinicSimulationForm.before_image) {
        throw new Error('Selecciona tu caso, el objetivo y sube una foto');
      }
      const body = new FormData();
      body.append('simulation_goal', clinicSimulationForm.simulation_goal);
      body.append('simulation_notes', clinicSimulationForm.simulation_notes);
      body.append('before_image', clinicSimulationForm.before_image);
      const res = await authFetch(`${API}/clinic/leads/${clinicSimulationForm.lead_id}/simulate`, { method: 'POST', body });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo generar la simulación');
      setClinicSimulationForm({ lead_id: '', simulation_goal: '', simulation_notes: '', before_image: null });
      await loadDashboard();
      alert('Simulación IA generada correctamente');
    } catch (error) {
      alert(error.message || 'No se pudo generar la simulación');
    } finally {
      setFinanceLoading(false);
    }
  };

  if (loading) return null;
  if (!user) return null;

  const visibleTabs = isClinicalPatient ? PATIENT_TABS : isPartnerOrAsociado ? PARTNER_TABS : TABS;
  const roleLabel = user.role === 'agencia' ? 'Agencia' : user.role === 'asociado' ? 'Asociado' : user.role === 'partner' ? 'Partner' : 'Usuario';
  const roleColor = user.role === 'agencia'
    ? 'bg-indigo-500/20 text-indigo-300'
    : user.role === 'asociado'
      ? 'bg-secondary/20 text-secondary'
      : user.role === 'partner'
        ? 'bg-primary/20 text-primary'
        : 'bg-gray-500/20 text-gray-400';

  return (
    <>
      <SEO title="Mi Panel — Neuralia369" description="Panel personal" />
      <div className="min-h-screen pt-20 pb-12 px-4" data-testid="user-dashboard">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-[#0A1A3F] text-xl font-heading font-bold">{user.nombre?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-text-heading">{user.nombre}</h1>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    roleColor
                  }`}>
                    {roleLabel}
                  </span>
                  <span className="text-text-muted text-sm">{user.email}</span>
                </div>
              </div>
            </div>
            <button onClick={handleLogout} className="btn-secondary text-sm py-2 px-4" data-testid="user-logout">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>

          {isClinicalPatient && (
            <div className="glass-card tracing-panel overflow-hidden mb-8 bg-[radial-gradient(circle_at_top_left,rgba(197,168,128,0.16),transparent_28%),linear-gradient(180deg,rgba(17,17,17,0.98),rgba(10,10,10,0.94))]" data-testid="patient-clinical-hero-card">
              <div className="grid gap-6 p-6 lg:grid-cols-[1.12fr_0.88fr] lg:p-8">
                <div>
                  <p className="editorial-kicker mb-4" data-testid="patient-clinical-hero-kicker">Portal Clínico</p>
                  <h2 className="text-3xl font-semibold text-text-heading" data-testid="patient-clinical-hero-heading">Tu proceso clínico, tus imágenes y tus siguientes pasos en una sola vista</h2>
                  <p className="mt-4 max-w-2xl text-sm text-text-body" data-testid="patient-clinical-hero-subheading">Hemos preparado un espacio elegante, claro y privado para que puedas revisar tu etapa clínica, subir fotos y visualizar simulaciones IA orientativas con total transparencia.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-[20px] border border-border-subtle bg-black/25 p-4" data-testid="patient-clinical-hero-pill-privacy">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Privado</p>
                    <p className="mt-2 text-sm text-text-heading">Acceso seguro a tus casos</p>
                  </div>
                  <div className="rounded-[20px] border border-border-subtle bg-black/25 p-4" data-testid="patient-clinical-hero-pill-visual">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Visual</p>
                    <p className="mt-2 text-sm text-text-heading">Antes / después IA orientativo</p>
                  </div>
                  <div className="rounded-[20px] border border-border-subtle bg-black/25 p-4" data-testid="patient-clinical-hero-pill-clarity">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Claro</p>
                    <p className="mt-2 text-sm text-text-heading">Estado clínico y próximo paso</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="user-tabs">
            {visibleTabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-heading font-medium whitespace-nowrap transition-all ${
                  tab === t.id ? 'bg-primary text-[#0A1A3F]' : 'bg-card text-text-muted border border-border-subtle hover:border-primary/40'
                }`} data-testid={`user-tab-${t.id}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {tab === 'workspace' && (
            <WorkspaceHub authFetch={authFetch} />
          )}

          {tab === 'tesix' && (
            <Neuralia369TesixHub authFetch={authFetch} />
          )}

          {tab === 'connections' && (
            <EmbeddedConnectionsHub authFetch={authFetch} mode="client" />
          )}

          {tab === 'office' && isPartnerOrAsociado && dashboard?.partner_growth_office && (
            <PartnerGrowthOffice office={{ ...dashboard.partner_growth_office, authFetch, onRefresh: loadDashboard }} />
          )}

          {/* Overview */}
          {tab === 'overview' && (
            <div className="space-y-6">
              {dashboard ? (
                <>
                  {isPartnerOrAsociado && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className="glass-card p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-text-muted text-xs uppercase tracking-wider">Conversaciones</p>
                        <p className="text-2xl font-heading font-bold text-text-heading">{dashboard.total_conversations}</p>
                      </div>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                      className="glass-card p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-secondary" />
                      </div>
                      <div>
                        <p className="text-text-muted text-xs uppercase tracking-wider">Agentes Asignados</p>
                        <p className="text-2xl font-heading font-bold text-text-heading">
                          {dashboard.assigned_agents.length || 'Todos'}
                        </p>
                      </div>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                      className="glass-card p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                        <Activity className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <p className="text-text-muted text-xs uppercase tracking-wider">Agentes Activos</p>
                        <p className="text-2xl font-heading font-bold text-text-heading">{dashboard.agent_stats.length}</p>
                      </div>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                      className="glass-card p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                        <BadgePercent className="w-6 h-6 text-indigo-300" />
                      </div>
                      <div>
                        <p className="text-text-muted text-xs uppercase tracking-wider">Tarifa Especial</p>
                        <p className="text-2xl font-heading font-bold text-text-heading">{dashboard.pricing_profile?.discount_percent || 0}%</p>
                      </div>
                    </motion.div>
                  </div>
                  )}

                  {isPartnerOrAsociado && <div className="glass-card p-6" data-testid="private-pricing-profile">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                          {user.role === 'agencia' ? <Building2 className="w-6 h-6 text-primary" /> : <Sparkles className="w-6 h-6 text-primary" />}
                        </div>
                        <div>
                          <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Perfil comercial</p>
                          <h3 className="text-text-heading font-heading font-semibold text-lg">{dashboard.pricing_profile?.label}</h3>
                          <p className="text-text-body text-sm mt-1">{dashboard.pricing_profile?.description}</p>
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Descuento</p>
                        <p className="text-primary text-2xl font-heading font-bold">{dashboard.pricing_profile?.discount_percent || 0}%</p>
                        <Link to={`/precios?segment=${dashboard.pricing_profile?.role || 'publico'}`} className="text-primary text-sm hover:underline mt-2 inline-block" data-testid="private-pricing-link">
                          Ver precios aplicados
                        </Link>
                      </div>
                    </div>
                  </div>}

                  {dashboard.client_portal && (
                    <div className="space-y-6" data-testid="client-portal-overview-section">
                      {!isPartnerOrAsociado && !isClinicalPatient && dashboard.wallet && (
                        <div className="glass-card p-6 border border-primary/15 bg-primary/5" data-testid="user-private-wallet-panel">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                              <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Wallet privado</p>
                              <h3 className="text-text-heading font-heading font-semibold text-lg">Tu capacidad operativa privada dentro de Neuralia</h3>
                              <p className="text-text-body text-sm mt-2">Gestiona aquí tus recargas privadas y evita frenar la operación cuando el saldo baje.</p>
                            </div>
                            <div className="rounded-[20px] border border-border-subtle bg-card/40 px-4 py-3" data-testid="user-private-wallet-balance-box">
                              <p className="text-text-muted text-xs uppercase tracking-wider">Disponible</p>
                              <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.wallet.credits_available || 0}</p>
                            </div>
                          </div>

                          <div className="mt-5 grid gap-4 md:grid-cols-4">
                            <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="user-private-wallet-metric-available">
                              <p className="text-text-muted text-xs uppercase tracking-wider">Créditos disponibles</p>
                              <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.wallet.credits_available || 0}</p>
                            </div>
                            <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="user-private-wallet-metric-total">
                              <p className="text-text-muted text-xs uppercase tracking-wider">Capacidad total</p>
                              <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.wallet.credits_total || 0}</p>
                            </div>
                            <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="user-private-wallet-metric-included">
                              <p className="text-text-muted text-xs uppercase tracking-wider">Incluidos</p>
                              <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.wallet.credits_included || 0}</p>
                            </div>
                            <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="user-private-wallet-metric-purchased">
                              <p className="text-text-muted text-xs uppercase tracking-wider">Recargados</p>
                              <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.wallet.credits_purchased || 0}</p>
                            </div>
                          </div>

                          <div className="mt-5" data-testid="user-private-wallet-progress">
                            <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.18em] text-text-muted">
                              <span>Uso actual</span>
                              <span>{dashboard.wallet.credit_utilization_percent || 0}%</span>
                            </div>
                            <div className="mt-3 h-2 overflow-hidden rounded-full bg-background/70">
                              <div className="h-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${Math.min(dashboard.wallet.credit_utilization_percent || 0, 100)}%` }}></div>
                            </div>
                          </div>

                          {dashboard.wallet.credit_alert?.show && (
                            <div className={`mt-5 rounded-[20px] border p-4 ${dashboard.wallet.credit_alert.level === 'critical' ? 'border-rose-400/20 bg-rose-500/10' : 'border-amber-400/20 bg-amber-500/10'}`} data-testid="user-private-wallet-alert">
                              <p className="text-sm font-semibold text-text-heading">Aviso de saldo</p>
                              <p className="mt-2 text-sm text-text-body">{dashboard.wallet.credit_alert.message}</p>
                            </div>
                          )}

                          <div className="mt-6 rounded-[22px] border border-primary/20 bg-primary/10 p-4" data-testid="user-private-wallet-topup-box">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-primary">Recarga privada</p>
                            <p className="mt-2 text-sm text-text-body">Elige un pack y te llevamos al checkout privado con Stripe LIVE.</p>
                            <div className="mt-4 grid gap-3 md:grid-cols-3">
                              {(dashboard.wallet.top_up_packs || []).map((pack) => (
                                <button
                                  key={pack.id}
                                  type="button"
                                  className="rounded-[18px] border border-primary/20 bg-background/60 p-4 text-left transition hover:-translate-y-1"
                                  onClick={() => launchTopUpCheckout(pack.id)}
                                  data-testid={`user-private-wallet-topup-${pack.id}`}
                                >
                                  <p className="text-sm font-semibold text-text-heading">{pack.name}</p>
                                  <p className="mt-1 text-xs text-text-muted">{pack.credits} créditos</p>
                                  <p className="mt-3 text-lg font-bold text-primary">${pack.price}</p>
                                  <p className="mt-2 text-xs text-text-body">{topUpLoading === pack.id ? 'Redirigiendo...' : pack.description}</p>
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="mt-6 rounded-[22px] border border-border-subtle bg-background/45 p-4" data-testid="user-private-wallet-history-box">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Historial de recargas</p>
                            <div className="mt-4 space-y-3">
                              {(dashboard.wallet.history || []).length === 0 && (
                                <p className="text-sm text-text-body">Todavía no hay recargas acreditadas o iniciadas en este wallet.</p>
                              )}
                              {(dashboard.wallet.history || []).map((item) => (
                                <div key={item.id} className="rounded-[18px] border border-border-subtle bg-card/40 p-4" data-testid={`user-private-wallet-history-${item.id}`}>
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                      <p className="text-sm font-semibold text-text-heading">{item.package_name || 'Recarga privada'}</p>
                                      <p className="mt-1 text-xs text-text-muted">{new Date(item.created_at).toLocaleString('es-ES')} · {item.credits} créditos</p>
                                    </div>
                                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${item.payment_status === 'paid' ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>{item.payment_status}</span>
                                  </div>
                                  <p className="mt-3 text-sm text-text-body">Importe: ${item.amount} {(item.currency || 'usd').toUpperCase()}</p>
                                  {item.credits_applied && <p className="mt-2 text-xs text-primary">Saldo tras aplicación: {item.credits_balance_after}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="glass-card p-6 border border-primary/15 bg-primary/5" data-testid="client-ai-transparency-notice">
                        <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Transparencia IA</p>
                        <h3 className="text-text-heading font-heading font-semibold text-lg">Cómo usamos IA en tu cuenta</h3>
                        <p className="text-text-body text-sm mt-3">Neuralia369 puede usar automatización, agentes y asistencia algorítmica para acelerar análisis, contenido, seguimiento y operaciones. Las acciones sensibles mantienen supervisión humana y los entregables importantes deben revisarse antes de su uso legal, contractual, financiero o reputacional.</p>
                      </div>

                      <div className="glass-card p-6" data-testid="client-services-panel">
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                          <div>
                            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Servicios y seguimiento</p>
                            <h3 className="text-text-heading font-heading font-semibold text-lg">Lo que has contratado y cómo va</h3>
                            <p className="text-text-body text-sm mt-1">Visibilidad clara de servicios activos, progreso y siguiente paso.</p>
                          </div>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                          {(dashboard.client_portal.services || []).map((service) => (
                            <div key={service.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`client-service-card-${service.id}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-text-heading font-medium">{service.name}</p>
                                  <p className="text-text-muted text-xs mt-1">{service.billing_cycle || 'servicio'}</p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full border ${getStatusClass(service.status)}`}>{service.status || 'activo'}</span>
                              </div>
                              {service.service_goal && <p className="mt-3 text-sm text-text-body">{service.service_goal}</p>}
                              <p className="mt-3 text-primary text-sm font-semibold">{service.amount} {(service.currency || 'usd').toUpperCase()}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid gap-6 xl:grid-cols-2">
                        <div className="glass-card p-6" data-testid="client-agent-actions-panel">
                          <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Acciones de agentes</p>
                          <h3 className="text-text-heading font-heading font-semibold text-lg">Timeline de trabajo interno</h3>
                          <div className="mt-5 space-y-4">
                            {(dashboard.client_portal.operations_timeline || []).map((item) => (
                              <div key={`${item.type}-${item.id}`} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`client-timeline-item-${item.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div>
                                    <p className="text-text-heading font-medium">{item.title}</p>
                                    <p className="text-text-muted text-xs mt-1">{new Date(item.timestamp).toLocaleString('es-ES')}</p>
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded-full border ${getStatusClass(item.status)}`}>{item.status}</span>
                                </div>
                                <p className="mt-3 text-sm text-text-body">{item.result}</p>
                                {item.roadmap_step && <p className="mt-2 text-xs text-text-muted" data-testid={`client-timeline-roadmap-step-${item.id}`}>Paso roadmap: {item.roadmap_step}</p>}
                                {typeof item.progress_percent === 'number' && (
                                  <div className="mt-3" data-testid={`client-timeline-progress-${item.id}`}>
                                    <div className="h-2 rounded-full bg-background/70 overflow-hidden">
                                      <div className="h-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${item.progress_percent}%` }}></div>
                                    </div>
                                    <p className="mt-1 text-xs text-text-muted">Progreso {item.progress_percent}%</p>
                                  </div>
                                )}
                                {item.validation_required && (
                                  <div className="mt-3 rounded-2xl border border-amber-400/20 bg-amber-500/10 p-3" data-testid={`client-timeline-validation-${item.id}`}>
                                    <p className="text-xs uppercase tracking-[0.18em] text-amber-300">Revisión humana</p>
                                    <p className="mt-1 text-sm text-text-body">Estado de validación: {item.validation_status || 'pendiente'}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-6">
                          {!!dashboard.client_portal.ceo_orders?.length && (
                            <div className="glass-card p-6" data-testid="client-ceo-roadmaps-panel">
                              <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Roadmaps ejecutivos</p>
                              <h3 className="text-text-heading font-heading font-semibold text-lg">Órdenes del CEO en ejecución</h3>
                              <div className="mt-5 space-y-3">
                                {(dashboard.client_portal.ceo_orders || []).map((order) => (
                                  <div key={order.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`client-ceo-order-${order.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-text-heading font-medium">{order.title}</p>
                                        <p className="text-text-muted text-xs mt-1">{order.objective_type} · prioridad {order.priority}</p>
                                      </div>
                                      <span className={`text-xs px-2 py-1 rounded-full border ${getStatusClass(order.roadmap_status || order.status)}`}>{order.roadmap_status || order.status}</span>
                                    </div>
                                    <div className="mt-4 h-2 rounded-full bg-background/70 overflow-hidden" data-testid={`client-ceo-order-progress-${order.id}`}>
                                      <div className="h-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${order.roadmap_summary?.progress_percent || 0}%` }}></div>
                                    </div>
                                    <p className="mt-2 text-sm text-text-body">{order.roadmap_summary?.completed_tasks || 0} de {order.roadmap_summary?.total_tasks || 0} tareas completadas · progreso {order.roadmap_summary?.progress_percent || 0}%</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="glass-card p-6" data-testid="client-payments-panel">
                            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Pagos</p>
                            <h3 className="text-text-heading font-heading font-semibold text-lg">Historial económico</h3>
                            <div className="mt-5 space-y-3">
                              {(dashboard.client_portal.payments || []).map((payment) => (
                                <div key={payment.session_id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 flex items-start justify-between gap-3" data-testid={`client-payment-${payment.session_id}`}>
                                  <div>
                                    <p className="text-text-heading font-medium">{payment.package_name}</p>
                                    <p className="text-text-muted text-xs mt-1">{new Date(payment.created_at).toLocaleDateString('es-ES')}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-primary font-semibold">{payment.amount} {(payment.currency || 'usd').toUpperCase()}</p>
                                    <p className="text-text-muted text-xs mt-1">{payment.billing_cycle || payment.payment_type || 'pago'}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="glass-card p-6" data-testid="client-contracts-panel">
                            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Contratos</p>
                            <h3 className="text-text-heading font-heading font-semibold text-lg">Estado contractual</h3>
                            <div className="mt-5 space-y-3">
                              {(dashboard.client_portal.contracts || []).map((contract) => (
                                <div key={contract.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 flex items-start justify-between gap-3" data-testid={`client-contract-${contract.id}`}>
                                  <div>
                                    <p className="text-text-heading font-medium">{contract.name}</p>
                                    <p className="text-text-muted text-xs mt-1">Actualizado: {new Date(contract.updated_at || contract.created_at).toLocaleDateString('es-ES')}</p>
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded-full border ${getStatusClass(contract.status)}`}>{contract.status || 'draft'}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="glass-card p-6" data-testid="client-finance-panel">
                            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Documentación financiera</p>
                            <h3 className="text-text-heading font-heading font-semibold text-lg">Facturas, extractos y base contable</h3>
                            <p className="text-text-body text-sm mt-2">Sube aquí facturas, extractos, contratos, presupuestos e impuestos para construir tu memoria administrativa y automatizar procesos futuros.</p>

                            <div className="mt-5 grid gap-4 md:grid-cols-4">
                              <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="client-finance-metric-docs">
                                <p className="text-text-muted text-xs uppercase tracking-wider">Documentos</p>
                                <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.client_portal.finance_workspace?.metrics?.total_documents || 0}</p>
                              </div>
                              <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="client-finance-metric-invoices">
                                <p className="text-text-muted text-xs uppercase tracking-wider">Facturas</p>
                                <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.client_portal.finance_workspace?.metrics?.invoices || 0}</p>
                              </div>
                              <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="client-finance-metric-bank">
                                <p className="text-text-muted text-xs uppercase tracking-wider">Extractos</p>
                                <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.client_portal.finance_workspace?.metrics?.bank_statements || 0}</p>
                              </div>
                              <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="client-finance-metric-volume">
                                <p className="text-text-muted text-xs uppercase tracking-wider">Volumen</p>
                                <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.client_portal.finance_workspace?.metrics?.tracked_invoice_volume || 0} EUR</p>
                              </div>
                            </div>

                            <div className="mt-6 grid gap-6 xl:grid-cols-2">
                              <form onSubmit={uploadFinancialDocument} className="space-y-3" data-testid="client-finance-upload-form">
                                <h4 className="text-text-heading font-heading font-semibold">Subir documento</h4>
                                <input className="input-kinesthetic" placeholder="Título" value={financeForm.title} onChange={(e) => setFinanceForm((prev) => ({ ...prev, title: e.target.value }))} data-testid="finance-title-input" />
                                <div className="grid md:grid-cols-2 gap-3">
                                  <select className="input-kinesthetic" value={financeForm.document_type} onChange={(e) => setFinanceForm((prev) => ({ ...prev, document_type: e.target.value }))} data-testid="finance-document-type-select">
                                    {(dashboard.client_portal.finance_workspace?.document_types || []).map((type) => <option key={type} value={type}>{type}</option>)}
                                  </select>
                                  <input type="file" className="input-kinesthetic" onChange={(e) => setFinanceForm((prev) => ({ ...prev, document_file: e.target.files?.[0] || null }))} data-testid="finance-file-input" />
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <input className="input-kinesthetic" placeholder="Importe" value={financeForm.amount} onChange={(e) => setFinanceForm((prev) => ({ ...prev, amount: e.target.value }))} data-testid="finance-amount-input" />
                                  <input className="input-kinesthetic" placeholder="Moneda" value={financeForm.currency} onChange={(e) => setFinanceForm((prev) => ({ ...prev, currency: e.target.value }))} data-testid="finance-currency-input" />
                                  <input type="date" className="input-kinesthetic" value={financeForm.issue_date} onChange={(e) => setFinanceForm((prev) => ({ ...prev, issue_date: e.target.value }))} data-testid="finance-issue-date-input" />
                                  <input type="date" className="input-kinesthetic" value={financeForm.due_date} onChange={(e) => setFinanceForm((prev) => ({ ...prev, due_date: e.target.value }))} data-testid="finance-due-date-input" />
                                </div>
                                <input className="input-kinesthetic" placeholder="Proveedor / contraparte" value={financeForm.counterparty} onChange={(e) => setFinanceForm((prev) => ({ ...prev, counterparty: e.target.value }))} data-testid="finance-counterparty-input" />
                                <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Notas" value={financeForm.notes} onChange={(e) => setFinanceForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="finance-notes-input" />
                                <button type="submit" className="btn-primary" disabled={financeLoading} data-testid="finance-upload-submit-button"><Upload className="w-4 h-4" /> {financeLoading ? 'Guardando...' : 'Subir documento'}</button>
                              </form>

                              <form onSubmit={createFinanceNote} className="space-y-3" data-testid="client-finance-note-form">
                                <h4 className="text-text-heading font-heading font-semibold">Plan financiero / notas</h4>
                                <input className="input-kinesthetic" placeholder="Título" value={financeNote.title} onChange={(e) => setFinanceNote((prev) => ({ ...prev, title: e.target.value }))} data-testid="finance-note-title-input" />
                                <input className="input-kinesthetic" placeholder="Categoría" value={financeNote.category} onChange={(e) => setFinanceNote((prev) => ({ ...prev, category: e.target.value }))} data-testid="finance-note-category-input" />
                                <textarea className="input-kinesthetic resize-none" rows={6} placeholder="Añade decisiones, previsiones, alertas o ideas de caja/planificación" value={financeNote.content} onChange={(e) => setFinanceNote((prev) => ({ ...prev, content: e.target.value }))} data-testid="finance-note-content-input" />
                                <button type="submit" className="btn-secondary" disabled={financeLoading} data-testid="finance-note-submit-button"><Wallet className="w-4 h-4" /> Guardar nota</button>
                              </form>
                            </div>

                            <div className="mt-6 space-y-3" data-testid="client-finance-documents-list">
                              {(dashboard.client_portal.finance_workspace?.documents || []).map((doc) => (
                                <div key={doc.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`client-finance-document-${doc.id}`}>
                                  <div>
                                    <p className="text-text-heading font-medium">{doc.title}</p>
                                    <p className="text-text-muted text-xs mt-1">{doc.document_type} · {doc.original_filename}</p>
                                    <p className="text-text-body text-sm mt-1">{doc.counterparty || 'Sin contraparte'} {doc.amount ? `· ${doc.amount} ${(doc.currency || 'EUR').toUpperCase()}` : ''}</p>
                                  </div>
                                  <button type="button" className="btn-secondary text-sm" onClick={() => downloadFinancialDocument(doc.id, doc.original_filename)} data-testid={`client-finance-download-${doc.id}`}>
                                    <FileText className="w-4 h-4" /> Descargar
                                  </button>
                                </div>
                              ))}
                            </div>

                            <div className="mt-8 grid gap-6 xl:grid-cols-2">
                              <form onSubmit={createStockItem} className="space-y-3" data-testid="client-stock-form">
                                <h4 className="text-text-heading font-heading font-semibold">Control de stock</h4>
                                <input className="input-kinesthetic" placeholder="Producto / item" value={stockForm.name} onChange={(e) => setStockForm((prev) => ({ ...prev, name: e.target.value }))} data-testid="stock-name-input" />
                                <input className="input-kinesthetic" placeholder="SKU" value={stockForm.sku} onChange={(e) => setStockForm((prev) => ({ ...prev, sku: e.target.value }))} data-testid="stock-sku-input" />
                                <div className="grid md:grid-cols-3 gap-3">
                                  <input className="input-kinesthetic" type="number" placeholder="Actual" value={stockForm.current_units} onChange={(e) => setStockForm((prev) => ({ ...prev, current_units: e.target.value }))} data-testid="stock-current-input" />
                                  <input className="input-kinesthetic" type="number" placeholder="Mínimo" value={stockForm.minimum_units} onChange={(e) => setStockForm((prev) => ({ ...prev, minimum_units: e.target.value }))} data-testid="stock-minimum-input" />
                                  <input className="input-kinesthetic" type="number" placeholder="Reordenar" value={stockForm.reorder_units} onChange={(e) => setStockForm((prev) => ({ ...prev, reorder_units: e.target.value }))} data-testid="stock-reorder-input" />
                                </div>
                                <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Notas" value={stockForm.notes} onChange={(e) => setStockForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="stock-notes-input" />
                                <button type="submit" className="btn-secondary" disabled={financeLoading} data-testid="stock-submit-button">Guardar stock</button>
                              </form>

                              <form onSubmit={createPurchaseRequest} className="space-y-3" data-testid="client-purchase-form">
                                <h4 className="text-text-heading font-heading font-semibold">Compras y autorizaciones</h4>
                                <input className="input-kinesthetic" placeholder="Título" value={purchaseForm.title} onChange={(e) => setPurchaseForm((prev) => ({ ...prev, title: e.target.value }))} data-testid="purchase-title-input" />
                                <input className="input-kinesthetic" placeholder="Proveedor" value={purchaseForm.supplier} onChange={(e) => setPurchaseForm((prev) => ({ ...prev, supplier: e.target.value }))} data-testid="purchase-supplier-input" />
                                <div className="grid md:grid-cols-2 gap-3">
                                  <input className="input-kinesthetic" placeholder="Importe estimado" value={purchaseForm.estimated_amount} onChange={(e) => setPurchaseForm((prev) => ({ ...prev, estimated_amount: e.target.value }))} data-testid="purchase-amount-input" />
                                  <input className="input-kinesthetic" placeholder="Moneda" value={purchaseForm.currency} onChange={(e) => setPurchaseForm((prev) => ({ ...prev, currency: e.target.value }))} data-testid="purchase-currency-input" />
                                </div>
                                <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Motivo" value={purchaseForm.reason} onChange={(e) => setPurchaseForm((prev) => ({ ...prev, reason: e.target.value }))} data-testid="purchase-reason-input" />
                                <button type="submit" className="btn-secondary" disabled={financeLoading} data-testid="purchase-submit-button">Crear solicitud</button>
                              </form>
                            </div>

                            <div className="mt-8 grid gap-6 xl:grid-cols-2">
                              <div data-testid="client-stock-list">
                                <h4 className="text-text-heading font-heading font-semibold mb-3">Stock actual</h4>
                                {(dashboard.client_portal.finance_workspace?.stock_items || []).map((item) => (
                                  <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 mb-3" data-testid={`client-stock-item-${item.id}`}>
                                    <p className="text-text-heading font-medium">{item.name}</p>
                                    <p className="text-text-muted text-xs mt-1">{item.sku || 'Sin SKU'} · {item.current_units} uds · mínimo {item.minimum_units}</p>
                                  </div>
                                ))}
                              </div>

                              <div data-testid="client-purchase-list">
                                <h4 className="text-text-heading font-heading font-semibold mb-3">Solicitudes y autorizaciones</h4>
                                {(dashboard.client_portal.finance_workspace?.purchase_requests || []).map((item) => (
                                  <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 mb-3" data-testid={`client-purchase-item-${item.id}`}>
                                    <p className="text-text-heading font-medium">{item.title}</p>
                                    <p className="text-text-muted text-xs mt-1">{item.supplier || 'Sin proveedor'} · {item.estimated_amount} {item.currency}</p>
                                    <p className="text-text-body text-sm mt-2">Estado: {item.status}</p>
                                    {(item.status === 'needs_authorization' || item.status === 'pending') && (
                                      <div className="mt-3 flex gap-2">
                                        <button type="button" className="btn-secondary text-xs" onClick={() => updatePurchaseStatus(item.id, 'approved')} data-testid={`purchase-approve-${item.id}`}>Aprobar</button>
                                        <button type="button" className="btn-secondary text-xs" onClick={() => updatePurchaseStatus(item.id, 'rejected')} data-testid={`purchase-reject-${item.id}`}>Rechazar</button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-6" data-testid="client-clinic-portal-panel">
                            <div className="glass-card border border-white/10 bg-[#0A0A0C]/75 p-6 backdrop-blur-xl" data-testid="client-clinic-command-header">
                              <div className="grid gap-6 md:grid-cols-8">
                                <div className="md:col-span-5">
                                  <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Portal clínico visual</p>
                                  <h3 className="text-text-heading font-heading font-semibold text-2xl">{primaryClinicalCase?.stage ? `Fase actual: ${primaryClinicalCase.stage}` : 'Tu proceso clínico, explicado con claridad'}</h3>
                                  <p className="text-text-body text-sm mt-3 max-w-2xl">{primaryClinicalCase?.next_action || 'Aquí verás tu siguiente paso, tus documentos, tus citas y las simulaciones visuales orientativas de forma mucho más clara.'}</p>
                                  <p className="mt-3 text-sm text-text-body">Puedes subir una foto para generar una <strong>{dashboard.client_portal.clinic_simulation_label || 'simulación IA orientativa'}</strong>. Es una visualización de consulta/comercial y no sustituye criterio médico ni garantiza un resultado exacto.</p>
                                </div>
                                <div className="grid gap-4 md:col-span-3" data-testid="client-clinic-command-metrics">
                                  <div className="rounded-[22px] border border-primary/20 bg-primary/10 p-5" data-testid="client-clinic-command-next-step-card">
                                    <p className="text-primary text-xs uppercase tracking-wider">Tu siguiente paso</p>
                                    <p className="mt-3 text-lg font-heading font-semibold text-text-heading">{primaryClinicalCase?.next_action || 'Pendiente de actualización por tu clínica'}</p>
                                  </div>
                                  <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-command-next-event-card">
                                    <p className="text-text-muted text-xs uppercase tracking-wider">Próximo evento</p>
                                    <p className="mt-3 text-base font-heading font-semibold text-text-heading">{primaryUpcomingEvent?.title || 'Sin cita confirmada todavía'}</p>
                                    <p className="mt-2 text-sm text-text-body">{primaryUpcomingEvent?.scheduled_at ? new Date(primaryUpcomingEvent.scheduled_at).toLocaleString('es-ES') : 'Cuando tu clínica programe una revisión, la verás aquí.'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="glass-card border border-white/10 bg-[#0A0A0C]/70 p-4 backdrop-blur-xl" data-testid="client-clinic-view-switcher-card">
                              <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                  <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Vista del portal</p>
                                  <p className="text-sm text-text-body" data-testid="client-clinic-active-view-helper">{activePatientPortalView.helper}</p>
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid="client-clinic-view-switcher">
                                  {PATIENT_PORTAL_VIEWS.map(({ id, label }) => (
                                    <button
                                      key={id}
                                      type="button"
                                      onClick={() => setPatientClinicView(id)}
                                      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${patientClinicView === id ? 'bg-primary text-[#050505]' : 'border border-white/10 bg-card text-text-muted hover:border-primary/30 hover:text-text-heading'}`}
                                      data-testid={`client-clinic-view-switch-${id}`}
                                    >
                                      {label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {patientClinicView === 'resumen' && <>
                            <div className="mt-4 grid gap-6 md:grid-cols-8" data-testid="client-clinic-summary-tetris-grid">
                              <div className="md:col-span-5 space-y-4">
                                <div className="grid gap-3 md:grid-cols-3" data-testid="client-clinic-story-list">
                                  {(dashboard.client_portal.clinic_story || []).map((item, index) => (
                                    <div key={index} className="rounded-[20px] border border-border-subtle bg-black/20 p-4 text-sm text-text-body" data-testid={`client-clinic-story-${index}`}>
                                      {item}
                                    </div>
                                  ))}
                                </div>

                                <div className="grid gap-4 md:grid-cols-2" data-testid="client-clinic-summary-main-grid">
                                  <div className="rounded-[22px] border border-primary/20 bg-primary/10 p-5" data-testid="client-clinic-status-card">
                                    <p className="text-primary text-xs uppercase tracking-wider">Estado actual</p>
                                    <p className="mt-3 text-xl font-heading font-semibold text-text-heading">{primaryClinicalCase?.stage || 'Pendiente de actualización'}</p>
                                    <p className="mt-2 text-sm text-text-body">{primaryClinicalCase?.treatment_interest || 'Tu clínica actualizará aquí el tratamiento o caso activo.'}</p>
                                  </div>
                                  <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-next-step-card">
                                    <p className="text-text-muted text-xs uppercase tracking-wider">Siguiente paso recomendado</p>
                                    <p className="mt-3 text-lg font-heading font-semibold text-text-heading">{primaryClinicalCase?.next_action || 'Tu clínica te indicará aquí el siguiente paso importante.'}</p>
                                  </div>
                                  <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-summary-event-highlight">
                                    <div className="flex items-center gap-2 text-text-heading"><BellRing className="w-4 h-4 text-primary" /><p className="text-xs uppercase tracking-wider text-text-muted">Próxima cita</p></div>
                                    {primaryUpcomingEvent ? (
                                      <>
                                        <p className="mt-3 text-lg font-heading font-semibold text-text-heading">{primaryUpcomingEvent.title}</p>
                                        <p className="mt-2 text-sm text-text-body">{primaryUpcomingEvent.scheduled_at ? new Date(primaryUpcomingEvent.scheduled_at).toLocaleString('es-ES') : 'Pendiente de fecha'}</p>
                                        <p className="mt-2 text-sm text-text-body">{primaryUpcomingEvent.location || 'Ubicación pendiente'}</p>
                                      </>
                                    ) : <p className="mt-3 text-sm text-text-body">Cuando tu clínica programe una cita o revisión, la verás aquí.</p>}
                                  </div>
                                  <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-summary-news-highlight">
                                    <div className="flex items-center gap-2 text-text-heading"><Newspaper className="w-4 h-4 text-primary" /><p className="text-xs uppercase tracking-wider text-text-muted">Novedad útil</p></div>
                                    {primaryClinicalNews ? (
                                      <>
                                        <p className="mt-3 text-lg font-heading font-semibold text-text-heading">{primaryClinicalNews.title}</p>
                                        <p className="mt-2 text-sm text-text-body">{primaryClinicalNews.excerpt}</p>
                                      </>
                                    ) : <p className="mt-3 text-sm text-text-body">Tu clínica puede compartir aquí recomendaciones, boletines y cuidados útiles.</p>}
                                  </div>
                                </div>
                              </div>

                              <div className="md:col-span-3 space-y-4">
                                <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-summary-metrics-card">
                                  <p className="text-text-muted text-xs uppercase tracking-wider">Resumen rápido</p>
                                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid="client-clinic-metric-cases">
                                      <p className="text-text-muted text-xs uppercase tracking-wider">Casos</p>
                                      <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.client_portal.clinic_cases?.length || 0}</p>
                                    </div>
                                    <div className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid="client-clinic-metric-simulations">
                                      <p className="text-text-muted text-xs uppercase tracking-wider">Simulaciones</p>
                                      <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.client_portal.clinic_simulations?.length || 0}</p>
                                    </div>
                                    <div className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid="client-clinic-metric-documents">
                                      <p className="text-text-muted text-xs uppercase tracking-wider">Documentos</p>
                                      <p className="mt-2 text-2xl font-heading font-bold text-text-heading">{dashboard.client_portal.clinic_documents?.length || 0}</p>
                                    </div>
                                    <div className="rounded-[18px] border border-primary/20 bg-primary/10 p-4" data-testid="client-clinic-metric-label">
                                      <p className="text-primary text-xs uppercase tracking-wider">Visual</p>
                                      <p className="mt-2 text-sm font-heading font-bold text-text-heading">{dashboard.client_portal.clinic_simulation_label || 'Simulación IA'}</p>
                                    </div>
                                  </div>
                                </div>

                                <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-document-communication-grid">
                                  <div className="space-y-4">
                                    <div data-testid="client-clinic-documents-highlight-card">
                                      <div className="flex items-center gap-2 text-text-heading"><FileText className="w-4 h-4 text-primary" /><p className="text-xs uppercase tracking-wider text-text-muted">Tus documentos</p></div>
                                      {(dashboard.client_portal.clinic_documents || []).length ? (
                                        <div className="mt-3 space-y-2">
                                          {dashboard.client_portal.clinic_documents.slice(0, 2).map((item) => (
                                            <a key={item.id} href={item.download_url} target="_blank" rel="noreferrer" className="block rounded-[16px] border border-border-subtle bg-background/55 p-3 text-sm text-text-body" data-testid={`client-clinic-document-highlight-${item.id}`}>
                                              <div className="font-medium text-text-heading">{item.title}</div>
                                              <div className="mt-1 text-xs text-text-muted">{item.document_type}</div>
                                            </a>
                                          ))}
                                        </div>
                                      ) : <p className="mt-3 text-sm text-text-body">Tu clínica podrá compartir aquí consentimientos, presupuestos e informes.</p>}
                                    </div>

                                    <div data-testid="client-clinic-communication-highlight-card">
                                      <div className="flex items-center gap-2 text-text-heading"><BellRing className="w-4 h-4 text-primary" /><p className="text-xs uppercase tracking-wider text-text-muted">Comunicaciones preparadas</p></div>
                                      {(dashboard.client_portal.clinic_communications || []).length ? (
                                        <div className="mt-3 space-y-2">
                                          {dashboard.client_portal.clinic_communications.slice(0, 2).map((item) => (
                                            <div key={item.id} className="rounded-[16px] border border-primary/20 bg-black/25 p-3" data-testid={`client-clinic-communication-highlight-${item.id}`}>
                                              <div className="font-medium text-text-heading">{item.subject || item.communication_type}</div>
                                              <div className="mt-1 text-xs text-text-muted">{item.channel} · {item.status}</div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : <p className="mt-3 text-sm text-text-body">Aquí verás recordatorios y comunicaciones que tu clínica prepare para ti.</p>}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            </>}

                            {patientClinicView === 'novedades' && <div className="mt-6 space-y-6" data-testid="client-clinic-news-view">
                              <div className="grid gap-4 md:grid-cols-2">
                                <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-events-card">
                                  <div className="flex items-center gap-2 text-text-heading"><BellRing className="w-5 h-5 text-primary" /><h4 className="font-heading font-semibold">Próximas citas y revisiones</h4></div>
                                  <div className="mt-4 space-y-3" data-testid="client-clinic-events-list">
                                    {(dashboard.client_portal.clinic_upcoming_events || []).length ? dashboard.client_portal.clinic_upcoming_events.map((item) => (
                                      <div key={item.id} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`client-clinic-event-${item.id}`}>
                                        <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                                        <p className="mt-2 text-xs text-text-muted">{item.event_type} · {item.scheduled_at ? new Date(item.scheduled_at).toLocaleString('es-ES') : 'Pendiente'}</p>
                                        <p className="mt-2 text-sm text-text-body">{item.location || 'Ubicación por definir'}</p>
                                      </div>
                                    )) : <div className="rounded-[18px] border border-dashed border-border-subtle bg-background/35 p-4 text-sm text-text-muted" data-testid="client-clinic-events-empty">Todavía no tienes eventos programados visibles.</div>}
                                  </div>
                                </div>

                                <div className="rounded-[22px] border border-primary/20 bg-primary/10 p-5" data-testid="client-clinic-news-help-card">
                                  <div className="flex items-center gap-2 text-text-heading"><Newspaper className="w-5 h-5 text-primary" /><h4 className="font-heading font-semibold">Boletines y recomendaciones</h4></div>
                                  <p className="mt-4 text-sm text-text-body">Aquí verás avisos manuales de tu clínica y recomendaciones fijas para prepararte mejor, resolver dudas y entender tu proceso con más calma.</p>
                                </div>
                              </div>

                              <div className="grid gap-4 md:grid-cols-2" data-testid="client-clinic-news-list">
                                {(dashboard.client_portal.clinic_news || []).length ? dashboard.client_portal.clinic_news.map((item) => (
                                  <div key={item.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-5 shadow-[0_0_0_1px_rgba(197,168,128,0.03),0_16px_34px_rgba(0,0,0,0.22)]" data-testid={`client-clinic-news-card-${item.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-text-heading font-medium">{item.title}</p>
                                        <p className="text-text-muted text-xs mt-1">{item.category || 'general'} · {item.treatment_focus || 'general'}</p>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        <span className="text-xs px-2 py-1 rounded-full border border-primary/20 bg-primary/10 text-primary">{item.news_type}</span>
                                        {item.pinned && <span className="text-xs px-2 py-1 rounded-full border border-emerald-400/20 bg-emerald-500/10 text-emerald-200">Destacado</span>}
                                      </div>
                                    </div>
                                    <p className="mt-4 text-sm text-text-body">{item.content || item.excerpt}</p>
                                    {(item.cta_label || item.cta_target) && <div className="mt-4 rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`client-clinic-news-cta-${item.id}`}>
                                      <p className="text-xs uppercase tracking-wider text-text-muted">{item.cta_label || 'Siguiente paso'}</p>
                                      <p className="mt-2 text-sm text-text-heading">{item.cta_target || 'Consulta esta novedad con tu coordinadora clínica.'}</p>
                                    </div>}
                                  </div>
                                )) : <div className="rounded-[22px] border border-dashed border-border-subtle bg-background/35 p-5 text-sm text-text-muted" data-testid="client-clinic-news-empty">Tu portal mostrará aquí noticias y recomendaciones cuando estén disponibles.</div>}
                              </div>

                              <div className="grid gap-4 md:grid-cols-2" data-testid="client-clinic-promotions-documents-grid">
                                <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-promotions-card">
                                  <div className="flex items-center gap-2 text-text-heading"><Mail className="w-5 h-5 text-primary" /><h4 className="font-heading font-semibold">Promociones y recordatorios</h4></div>
                                  <div className="mt-4 space-y-3">
                                    {(dashboard.client_portal.clinic_promotions || []).length ? dashboard.client_portal.clinic_promotions.map((item) => (
                                      <div key={item.id} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`client-clinic-promotion-${item.id}`}>
                                        <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                                        <p className="mt-2 text-xs text-text-muted">{item.channel} · {item.status}</p>
                                        <p className="mt-2 text-sm text-text-body">{item.message}</p>
                                      </div>
                                    )) : <div className="rounded-[18px] border border-dashed border-border-subtle bg-background/35 p-4 text-sm text-text-muted" data-testid="client-clinic-promotions-empty">Todavía no hay promociones visibles.</div>}
                                  </div>
                                </div>

                                <div className="rounded-[22px] border border-border-subtle bg-card/40 p-5" data-testid="client-clinic-documents-card">
                                  <div className="flex items-center gap-2 text-text-heading"><FileText className="w-5 h-5 text-primary" /><h4 className="font-heading font-semibold">Documentación compartida</h4></div>
                                  <div className="mt-4 space-y-3">
                                    {(dashboard.client_portal.clinic_documents || []).length ? dashboard.client_portal.clinic_documents.map((item) => (
                                      <a key={item.id} href={item.download_url} target="_blank" rel="noreferrer" className="block rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`client-clinic-document-${item.id}`}>
                                        <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                                        <p className="mt-2 text-xs text-text-muted">{item.document_type}</p>
                                        {item.notes && <p className="mt-2 text-sm text-text-body">{item.notes}</p>}
                                      </a>
                                    )) : <div className="rounded-[18px] border border-dashed border-border-subtle bg-background/35 p-4 text-sm text-text-muted" data-testid="client-clinic-documents-empty">Aún no hay documentos visibles para tu caso.</div>}
                                  </div>
                                </div>
                              </div>
                            </div>}

                            {patientClinicView === 'simulacion' && <div className="mt-6 grid gap-6 xl:grid-cols-2">
                              <form onSubmit={createClinicSimulation} className="space-y-3" data-testid="client-clinic-simulation-form">
                                <h4 className="text-text-heading font-heading font-semibold">Subir foto y generar simulación</h4>
                                <select className="input-kinesthetic" value={clinicSimulationForm.lead_id} onChange={(e) => setClinicSimulationForm((prev) => ({ ...prev, lead_id: e.target.value }))} data-testid="client-clinic-simulation-lead-select">
                                  <option value="">Selecciona tu caso</option>
                                  {(dashboard.client_portal.clinic_cases || []).map((lead) => (
                                    <option key={lead.id} value={lead.id}>{getPatientCaseLabel(lead)}</option>
                                  ))}
                                </select>
                                <input className="input-kinesthetic" placeholder="Objetivo visual de la simulación" value={clinicSimulationForm.simulation_goal} onChange={(e) => setClinicSimulationForm((prev) => ({ ...prev, simulation_goal: e.target.value }))} data-testid="client-clinic-simulation-goal-input" />
                                <textarea className="input-kinesthetic resize-none" rows={4} placeholder="Notas para la simulación" value={clinicSimulationForm.simulation_notes} onChange={(e) => setClinicSimulationForm((prev) => ({ ...prev, simulation_notes: e.target.value }))} data-testid="client-clinic-simulation-notes-input" />
                                <input type="file" accept="image/png,image/jpeg,image/webp" className="input-kinesthetic" onChange={(e) => setClinicSimulationForm((prev) => ({ ...prev, before_image: e.target.files?.[0] || null }))} data-testid="client-clinic-simulation-file-input" />
                                <button type="submit" className="btn-primary" disabled={financeLoading} data-testid="client-clinic-simulation-submit-button"><Upload className="w-4 h-4" /> {financeLoading ? 'Generando...' : 'Generar simulación IA'}</button>
                              </form>

                              <div className="space-y-3" data-testid="client-clinic-simulation-help-card">
                                <h4 className="text-text-heading font-heading font-semibold">Cómo usar esta parte</h4>
                                <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4 text-sm text-text-body">
                                  <p>1. Elige tu caso.</p>
                                  <p className="mt-2">2. Escribe qué objetivo visual quieres ver.</p>
                                  <p className="mt-2">3. Sube una foto clara.</p>
                                  <p className="mt-2">4. La clínica podrá revisar una simulación IA orientativa contigo.</p>
                                </div>
                              </div>
                            </div>}

                            {patientClinicView === 'casos' && <div className="mt-6 space-y-3" data-testid="client-clinic-cases-list">
                                <h4 className="text-text-heading font-heading font-semibold">Estado de tus casos</h4>
                                {(dashboard.client_portal.clinic_case_cards || dashboard.client_portal.clinic_cases || []).map((lead) => (
                                  <div key={lead.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4 shadow-[0_0_0_1px_rgba(197,168,128,0.03),0_16px_34px_rgba(0,0,0,0.22)]" data-testid={`client-clinic-case-${lead.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-text-heading font-medium">{lead.treatment_interest}</p>
                                        <p className="text-text-muted text-xs mt-1">Etapa: {lead.stage}</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-xs text-text-muted">Preop: <span className="text-text-heading">{lead.preop_status}</span></p>
                                        <p className="text-xs text-text-muted mt-1">Postop: <span className="text-text-heading">{lead.postop_status}</span></p>
                                      </div>
                                    </div>
                                    <p className="mt-3 text-sm text-text-body">{lead.next_action}</p>
                                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                      <div className="rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`client-clinic-case-value-${lead.id}`}>
                                        <p className="text-xs uppercase tracking-wider text-text-muted">Valor orientativo</p>
                                        <p className="mt-2 text-lg font-heading font-bold text-text-heading">€{Number(lead.value_estimate || 0).toLocaleString('es-ES')}</p>
                                      </div>
                                      <div className="rounded-[18px] border border-primary/20 bg-primary/10 p-3" data-testid={`client-clinic-case-simulations-${lead.id}`}>
                                        <p className="text-xs uppercase tracking-wider text-primary">Simulaciones</p>
                                        <p className="mt-2 text-lg font-heading font-bold text-text-heading">{lead.simulation_count || 0}</p>
                                      </div>
                                    </div>
                                    {lead.hero_simulation?.after_image_url && (
                                      <div className="mt-4 rounded-[18px] border border-primary/20 bg-black/25 p-3" data-testid={`client-clinic-case-hero-simulation-${lead.id}`}>
                                        <p className="text-xs uppercase tracking-wider text-primary">Última simulación</p>
                                        <img src={lead.hero_simulation.after_image_url} alt="Simulación reciente" className="mt-3 aspect-[5/4] w-full rounded-[16px] object-cover" data-testid={`client-clinic-case-hero-simulation-image-${lead.id}`} />
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>}

                            {patientClinicView === 'simulacion' && <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="client-clinic-simulation-gallery">
                              {(dashboard.client_portal.clinic_simulations || []).map((item) => (
                                <div key={item.id} className="rounded-[22px] border border-primary/15 bg-card/40 p-4 shadow-[0_0_0_1px_rgba(197,168,128,0.03),0_18px_40px_rgba(0,0,0,0.28)]" data-testid={`client-clinic-simulation-card-${item.id}`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <p className="text-text-heading font-medium">{item.simulation_goal}</p>
                                      <p className="text-text-muted text-xs mt-1">{dashboard.client_portal.clinic_simulation_label}</p>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full border ${getStatusClass(item.status)}`}>{item.status}</span>
                                  </div>
                                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                                    <div data-testid={`client-clinic-before-${item.id}`}>
                                      <p className="text-text-muted text-xs uppercase tracking-wider">Antes</p>
                                      {item.before_image_url ? <img src={item.before_image_url} alt="Antes" className="mt-2 aspect-[4/5] w-full rounded-[16px] object-cover" data-testid={`client-clinic-before-image-${item.id}`} /> : <div className="mt-2 flex aspect-[4/5] w-full items-center justify-center rounded-[16px] border border-dashed border-border-subtle text-sm text-text-muted">Sin imagen todavía</div>}
                                    </div>
                                    <div className="relative" data-testid={`client-clinic-after-${item.id}`}>
                                      <p className="text-primary text-xs uppercase tracking-wider">Después IA</p>
                                      <div className="absolute right-2 top-0 rounded-full border border-primary/20 bg-black/50 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-primary">IA</div>
                                      {item.after_image_url ? <img src={item.after_image_url} alt="Después IA" className="mt-2 aspect-[4/5] w-full rounded-[16px] object-cover" data-testid={`client-clinic-after-image-${item.id}`} /> : <div className="mt-2 flex aspect-[4/5] w-full items-center justify-center rounded-[16px] border border-dashed border-primary/20 text-sm text-text-muted">Se generará aquí</div>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isPartnerOrAsociado && dashboard.partner_exclusivity?.enabled && (
                    <div className="glass-card p-6 space-y-5" data-testid="partner-country-exclusivity-panel">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Exclusividad territorial</p>
                          <h3 className="text-text-heading font-heading font-semibold text-lg">Países reservados para tu panel</h3>
                          <p className="text-text-body text-sm mt-1">Los leads que entren con estos países se enrutan automáticamente a tu panel.</p>
                        </div>
                        <div className="text-left md:text-right">
                          <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Leads enroutados</p>
                          <p className="text-primary text-2xl font-heading font-bold" data-testid="partner-routed-leads-count">{dashboard.partner_exclusivity.routed_leads_count || 0}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2" data-testid="partner-country-tags">
                        {(dashboard.partner_exclusivity.countries || []).map((country) => (
                          <span key={country} className="rounded-full border border-secondary/30 bg-secondary/10 px-3 py-1 text-xs text-secondary">
                            {country}
                          </span>
                        ))}
                      </div>

                      {(dashboard.partner_exclusivity.country_rollup || []).length > 0 && (
                        <div className="grid gap-3 md:grid-cols-3" data-testid="partner-country-rollup-grid">
                          {dashboard.partner_exclusivity.country_rollup.map((item) => (
                            <div key={item.country} className="rounded-[20px] border border-border-subtle bg-card/40 p-4">
                              <p className="text-text-muted text-xs uppercase tracking-wider">{item.country}</p>
                              <p className="mt-2 text-text-heading text-2xl font-heading font-bold">{item.count}</p>
                              <p className="text-text-body text-sm mt-1">leads captados</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {(dashboard.partner_exclusivity.recent_leads || []).length > 0 && (
                        <div className="space-y-3" data-testid="partner-recent-routed-leads">
                          <h4 className="text-text-heading font-heading font-semibold">Últimos leads asignados</h4>
                          {dashboard.partner_exclusivity.recent_leads.map((lead) => (
                            <div key={lead.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-text-heading font-medium">{lead.nombre}</p>
                                <p className="text-text-muted text-sm">{lead.email}</p>
                                <p className="text-text-body text-sm mt-1">{lead.empresa || 'Empresa no indicada'}</p>
                              </div>
                              <div className="text-left md:text-right">
                                <p className="text-secondary text-sm font-medium">{lead.country || 'Sin país'}</p>
                                <p className="text-text-muted text-xs mt-1">{lead.routing_reason}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {isPartnerOrAsociado && dashboard.partner_exclusivity?.enabled && (
                    <TerritoryExecutiveMap
                      title="Mapa ejecutivo de países"
                      subtitle="Visualiza rápidamente qué territorios tienes reservados y dónde está entrando actividad comercial a tu panel."
                      items={buildPartnerTerritoryMapItems(dashboard.partner_exclusivity)}
                      testId="partner-territory-executive-map"
                    />
                  )}

                  {/* Agent Performance Chart */}
                  {isPartnerOrAsociado && dashboard.agent_stats.length > 0 && (
                    <div className="glass-card p-6">
                      <h3 className="text-text-heading font-heading font-semibold text-lg mb-4">Rendimiento por Agente</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboard.agent_stats}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="agent" tick={{ fill: '#64748B', fontSize: 12 }} />
                          <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                            {dashboard.agent_stats.map((_, i) => (
                              <motion.rect key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Recent conversations */}
                  {isPartnerOrAsociado && dashboard.recent_conversations.length > 0 && (
                    <div className="glass-card p-6">
                      <h3 className="text-text-heading font-heading font-semibold text-lg mb-4">Conversaciones Recientes</h3>
                      <div className="space-y-2">
                        {dashboard.recent_conversations.map((c, i) => {
                          const Icon = AGENT_ICONS[c.last_agent] || Sparkles;
                          return (
                            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-card border border-border-subtle">
                              <div className="flex items-center gap-3">
                                <Icon className="w-5 h-5 text-primary" />
                                <div>
                                  <p className="text-text-heading text-sm font-medium">
                                    {c.session_id.slice(0, 12)}...
                                  </p>
                                  <p className="text-text-muted text-xs">{c.count} msgs · {c.last_agent || 'GENERAL'}</p>
                                </div>
                              </div>
                              <span className="text-text-muted text-xs">{c.last_message?.slice(0, 10)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="glass-card p-8 text-center">
                  <Sparkles className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="text-text-heading font-heading font-semibold text-lg mb-2">Bienvenido a Neuralia369</h3>
                  <p className="text-text-muted">Tu panel personal. Aquí podrás ver tus citas y gestionar tu perfil.</p>
                </div>
              )}
            </div>
          )}

          {/* Profile */}
          {tab === 'profile' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 space-y-4" data-testid="user-profile">
              {user.must_change_password && (
                <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4" data-testid="must-change-password-banner">
                  <p className="text-xs uppercase tracking-[0.18em] text-amber-300">Acción recomendada</p>
                  <p className="mt-2 text-sm text-text-body">Tu cuenta se creó con una contraseña temporal. Cámbiala ahora para asegurar el acceso y dejar la cuenta lista para operar.</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wider block mb-1 font-body">Nombre</label>
                  <p className="text-text-heading text-lg font-heading">{user.nombre}</p>
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wider block mb-1 font-body">Email</label>
                  <p className="text-primary text-lg">{user.email}</p>
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wider block mb-1 font-body">Rol</label>
                  <span className={`text-sm px-3 py-1 rounded-full ${
                    roleColor
                  }`}>
                    {user.role === 'admin' ? 'Administrador' : roleLabel}
                  </span>
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wider block mb-1 font-body">Cuenta</label>
                  <p className="text-text-heading text-lg font-heading">{user.account_name || 'Neuralia Workspace'}</p>
                </div>
              </div>

              <form onSubmit={handleChangePassword} className="border-t border-border-subtle pt-4 space-y-4" data-testid="user-password-form">
                <div>
                  <h3 className="text-text-heading font-heading font-semibold text-lg">Cambiar contraseña</h3>
                  <p className="text-text-muted text-sm mt-1">Si recibiste una contraseña temporal al contratar, cámbiala aquí. Usa al menos 10 caracteres con mayúscula, minúscula, número y símbolo.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <PasswordInput minLength={6} placeholder="Contraseña actual" value={passwordForm.current_password} onChange={(e) => setPasswordForm((prev) => ({ ...prev, current_password: e.target.value }))} inputClassName="input-kinesthetic" testId="user-current-password-input" toggleTestId="user-current-password-visibility-toggle" />
                  <PasswordInput minLength={10} placeholder="Nueva contraseña" value={passwordForm.new_password} onChange={(e) => setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))} inputClassName="input-kinesthetic" testId="user-new-password-input" toggleTestId="user-new-password-visibility-toggle" />
                </div>
                <button type="submit" className="btn-primary justify-center" disabled={passwordLoading} data-testid="user-password-submit-button">
                  {passwordLoading ? 'Guardando...' : 'Actualizar contraseña'}
                </button>
              </form>
            </motion.div>
          )}

          {/* Bookings */}
          {tab === 'bookings' && (
            <div className="space-y-3" data-testid="user-bookings">
              {bookings.map((b, i) => (
                <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="glass-card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-text-heading text-base font-heading font-medium">{b.tipo_nombre || b.tipo}</p>
                      <p className="text-primary text-sm font-medium">{b.fecha} · {b.hora}</p>
                      <p className="text-text-muted text-xs">{b.duracion} min</p>
                      {b.consultant_name && <p className="text-text-body text-xs mt-1" data-testid={`user-booking-consultant-${b.id || i}`}>Consultor asignado: {b.consultant_name}</p>}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${b.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {b.status === 'confirmed' ? 'Confirmada' : b.status}
                  </span>
                </motion.div>
              ))}
              {bookings.length === 0 && (
                <div className="glass-card p-8 text-center">
                  <Calendar className="w-8 h-8 text-text-muted mx-auto mb-3" />
                  <p className="text-text-muted text-base">No tienes citas agendadas</p>
                  <p className="text-text-muted text-sm mt-1">Agenda una cita desde el chat o la página de agenda</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
